<template>
  <div>
    <SidebarSettingsGroup
      title="ScoreStream Widget"
      collapsible
      :element-type="element.type"
      data-test="graphics-scorestreamSettings-general"
    >
      <div class="sidebar-form-group">
        <label class="sidebar-form-label">
          Widget Type
        </label>
        <select
          v-model="widgetType"
          class="form-control"
          data-test="graphics-scorestreamSettings-widgetType"
          data-entity="graphics-scorestreamSettings-widgetType"
        >
          <option value="vert">
            Vertical Scoreboard
          </option>
          <option value="banner">
            Banner Scoreboard
          </option>
          <option value="teamSchedule">
            Team Schedule
          </option>
        </select>
      </div>

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">
          Widget ID
        </label>
        <validation-field as="input"
          v-model="widgetIdInput"
          rules="required|numeric"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          name="widgetId"
          class="form-control numeric-input"
          :class="{ 'is-invalid': validationErrors['widgetId'] }"
          data-test="graphics-scorestreamSettings-widgetId"
          data-entity="graphics-scorestreamSettings-widgetId"
          @blur="onWidgetIdBlur"
        />
      </div>

      <div class="sidebar-form-group">
        <button
          type="button"
          class="btn btn-style-b btn-sm"
          data-test="graphics-scorestream-refresh"
          data-entity="graphics-scorestream-refresh"
          @click="refreshFrame"
        >
          <span class="far fa-sync margin-right-xs" />
          Refresh
        </button>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-scorestreamSettings-styleAndAnimation"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <border-radius
          v-model:top-left="borderTopLeftRadius"
          v-model:top-right="borderTopRightRadius"
          v-model:bottom-left="borderBottomLeftRadius"
          v-model:bottom-right="borderBottomRightRadius"
        />
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">Background color</label>
        <label
          class="radio inline-radio"
          for="solid"
        >
          Solid
          <input
            id="solid"
            v-model="fillStyle"
            type="radio"
            value="solid"
            data-test="graphics-scorestreamSettings-fillStyleSolid"
            data-entity="graphics-scorestreamSettings-fillStyleSolid"
          >
          <div class="checkmark" />
        </label>

        <label
          class="radio inline-radio"
          for="gradient"
        >
          Gradient
          <input
            id="gradient"
            v-model="fillStyle"
            type="radio"
            value="gradient"
            data-test="graphics-scorestreamSettings-fillStyleGradient"
            data-entity="graphics-scorestreamSettings-fillStyleGradient"
          >
          <div class="checkmark" />
        </label>

        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker text-center"
          data-test="graphics-scorestreamSettings-fillStyleSolidColor"
        >
          <colorpicker
            v-model="backgroundColor"
            :is-mapped="checkMapped('backgroundColor')"
          />

          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('backgroundColor')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>

        <div
          v-else-if="fillStyle == 'gradient'"
          class="settings-row settings-row-xs"
          data-test="graphics-scorestreamSettings-fillStyleGradientControls"
        >
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number="gradientDirection"
                rules="required|numeric"
                type="number"
                name="gradientDirection"
                :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                data-test="graphics-scorestreamSettings-setGradientDirection"
                data-entity="graphics-scorestreamSettings-setGradientDirection"
              />
              <span class="input-group-addon symbol">°</span>
            </div>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker text-center">
            <colorpicker
              v-model="gradientColorOne"
              :is-mapped="checkMapped('gradientColorOne')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down"
              data-balloon-length="medium"
            >
              <span
                v-if="checkMapped('gradientColorOne')"
                class="fa fa-lock mapped-lock"
              />
            </span>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker text-center">
            <colorpicker
              v-model="gradientColorTwo"
              :is-mapped="checkMapped('gradientColorTwo')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down-right"
              data-balloon-length="medium"
            >
              <span
                v-if="checkMapped('gradientColorTwo')"
                class="fa fa-lock mapped-lock"
              />
            </span>
          </div>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <background-image-settings
        :element="element"
        :scroll-offset="scrollOffset"
        @modal="(opts) => $emit('modal', opts)"
      />

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-scorestreamSettings-toggleBorder"
            data-entity="graphics-scorestreamSettings-toggleBorder"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <template v-if="border">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <validation-field as="input"
                v-model.number="borderWidth"
                rules="required|numeric|min:0"
                type="number"
                name="borderWidth"
                min="0"
                :class="{ 'is-invalid': validationErrors['borderWidth'] }"
                data-test="graphics-scorestreamSettings-setBorderWidth"
                data-entity="graphics-scorestreamSettings-setBorderWidth"
              />
              <label class="sidebar-input-sub-label">Width</label>
            </div>
            <div class="settings-col-6 settings-col-xs colorpicker">
              <colorpicker
                v-model="borderColor"
                :is-mapped="checkMapped('borderColor')"
              />
              <label class="sidebar-input-sub-label">
                Color
                <span
                  data-balloon="This setting is controlled by data. Unmap data to customize."
                  data-balloon-pos="down-right"
                  data-balloon-length="medium"
                >
                  <span
                    v-if="checkMapped('borderColor')"
                    class="fa fa-lock mapped-lock"
                  />
                </span>
              </label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <box-shadow-settings
        :element="element"
        :data-provider="dataProvider"
        :scroll-offset="scrollOffset"
      />

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Blur
          <input
            v-model="blur"
            type="checkbox"
            data-test="graphics-scorestreamSettings-toggleBlur"
            data-entity="graphics-scorestreamSettings-toggleBlur"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <div
          v-if="blur"
          class="input-group"
          data-test="graphics-scorestreamSettings-setBlurSize"
        >
          <validation-field as="input"
            v-model.number="blurSize"
            rules="required|numeric|min:0"
            type="number"
            name="blurSize"
            min="0"
            :class="{ 'is-invalid': validationErrors['blurSize'] }"
            data-test="graphics-scorestreamSettings-setBlurSize"
            data-entity="graphics-scorestreamSettings-setBlurSize"
          />
          <span class="input-group-addon">px</span>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <animation-settings :element="element" />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';
import { useFormContext } from 'vee-validate';
import EventBus from '../../utils/event-bus';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import BackgroundImageSettings from './BackgroundImage.vue';
import BorderRadius from './BorderRadius.vue';

export default {
  emits: ['modal'],
  name: 'ScorestreamSettingsComponent',
  setup() {
    const { validateField } = useFormContext();
    return { validateFormField: validateField };
  },

  components: {
    AnimationSettings,
    OpacitySettings,
    BoxShadowSettings,
    BackgroundImageSettings,
    BorderRadius,
  },

  mixins: [SettingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },

    scrollOffset: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      widgetIdInput: '',
    };
  },

  watch: {
    widgetId: {
      immediate: true,
      handler(val) {
        this.widgetIdInput = val;
      },
    },
  },

  computed: {
    widgetType: {
      get() {
        return this.getPropertyValue('widgetType');
      },

      set(v) {
        this.updateOneElementValue('widgetType', v);
        this.updateUrl();
      },
    },

    widgetId: {
      get() {
        return this.getPropertyValue('widgetId');
      },

      set(v) {
        const sanitized = String(v || '').replace(/[^0-9]/g, '');
        this.updateOneElementValue('widgetId', sanitized);
        this.$nextTick(() => this.validateFormField('widgetId'));
      },
    },

    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },

      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },

    borderTopLeftRadius: {
      get() {
        return this.getPropertyValue('borderTopLeftRadius');
      },

      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },

    borderTopRightRadius: {
      get() {
        return this.getPropertyValue('borderTopRightRadius');
      },

      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },

    borderBottomRightRadius: {
      get() {
        return this.getPropertyValue('borderBottomRightRadius');
      },

      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },

    borderBottomLeftRadius: {
      get() {
        return this.getPropertyValue('borderBottomLeftRadius');
      },

      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },

    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle', { emptyValue: 'solid' });
      },

      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },

    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { mappedPath: 'backgroundColor' });
      },

      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },

    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },

      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },

    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { mappedPath: 'gradientColorOne' });
      },

      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },

    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { mappedPath: 'gradientColorTwo' });
      },

      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },

    border: {
      get() {
        return this.getPropertyValue('border');
      },

      set(v) {
        this.updateOneElementValue('border', v);
      },
    },

    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },

      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },

    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { mappedPath: 'borderColor' });
      },

      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },

    blur: {
      get() {
        return this.getPropertyValue('blur');
      },

      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },

    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },

      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },

  methods: {
    updateUrl() {
      const widgetType = this.getPropertyValue('widgetType');
      const widgetId = this.getPropertyValue('widgetId');

      let url = '';
      if (widgetType && widgetId) {
        const baseUrl = process.env.GRAPHICS_SPA;
        url = `${baseUrl}/scorestream.html?widgetType=${widgetType}&widgetId=${widgetId}`;
      }

      this.updateOneElementValue('url', url);
    },

    refreshFrame() {
      EventBus.emit('frame:refresh', { id: this.element.id });
    },

    onWidgetIdBlur() {
      this.widgetId = this.widgetIdInput;
      this.updateUrl();
    },
  },
};
</script>

<style lang="scss" scoped>
/* Hide number input spinners */
.numeric-input {
  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  -moz-appearance: textfield;
}
</style>
